import React from 'react';
import { TextCell } from '@/components/ui/TextCell';
import { Divider } from '@/components/ui';
import NeighborhoodItem from './NeighborhoodItem';
import type { Neighborhood } from '@repo/api';
import { IconLocation, IconMapLocation } from '@repo/svgs';
import { useStore } from '@/providers/StoreProvider';
import AllBoroughOption from './AllBoroughOption';
import { useAnalytics } from '@/hooks/useAnalytics';

export type ExtendedNeighborhood = Neighborhood & {
  parentId?: string | null;
  childrenIds?: string[];
};
export type NeighborhoodsByBorough = Record<string, ExtendedNeighborhood[]>;

export interface NeighborhoodGroupedListProps {
  searchQuery?: string;
  className?: string;
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  onSelectResult?: () => void;
}

export function NeighborhoodGroupedList({
  searchQuery,
  selectedNeighborhoodIds: propSelectedIds,
  onToggleNeighborhood: propToggleNeighborhood,
  onToggleNeighborhoodWithChildren: propToggleNeighborhoodWithChildren,
  onToggleBorough: propToggleBorough,
  onSelectResult,
}: NeighborhoodGroupedListProps) {
  const analytics = useAnalytics();
  const neighborhoodsByBorough = useStore(s => s.neighborhoods.neighborhoodsByBorough);
  const filterByQuery = useStore(s => s.neighborhoods.filterByQuery);
  const storeNeighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const storeToggleNeighborhood = useStore(s => s.listingsFilters.toggleNeighborhood);
  const storeToggleNeighborhoodWithChildren = useStore(
    s => s.listingsFilters.toggleNeighborhoodWithChildren,
  );
  const storeToggleBorough = useStore(s => s.listingsFilters.toggleBorough);
  const accordion = useStore(s => s.neighborhoods.neighborhoodAccordionData);

  const neighborhoodIds = propSelectedIds ?? storeNeighborhoodIds;
  const toggleNeighborhood = propToggleNeighborhood ?? storeToggleNeighborhood;
  const toggleNeighborhoodWithChildren =
    propToggleNeighborhoodWithChildren ?? storeToggleNeighborhoodWithChildren;
  const toggleBorough = propToggleBorough ?? storeToggleBorough;

  const filteredNeighborhoods = React.useMemo(() => {
    const q = searchQuery?.trim();
    return q ? filterByQuery(q) : neighborhoodsByBorough || {};
  }, [searchQuery, filterByQuery, neighborhoodsByBorough]);

  const selectedSet = new Set(neighborhoodIds);
  const isBoroughFullySelected = React.useCallback(
    (borough: string) => {
      const entry = (accordion as any)?.[borough];
      if (!entry) return false;
      const allIds: string[] = [];
      for (const p of entry.parents || []) allIds.push(p.id);
      for (const group of Object.values(entry.childrenByParent || {})) {
        for (const c of group as any[]) allIds.push(c.id);
      }
      return allIds.length > 0 && allIds.every(id => selectedSet.has(id));
    },
    [accordion, selectedSet],
  );

  const q = (searchQuery || '').toLowerCase().trim();
  const boroughs = React.useMemo(() => Object.keys(filteredNeighborhoods), [filteredNeighborhoods]);

  return (
    <>
      {boroughs.map((borough, boroughIndex) => {
        const neighborhoods = filteredNeighborhoods[borough];
        const showAllOption = q.length > 0 && borough.toLowerCase().includes(q);
        const allSelected = isBoroughFullySelected(borough);
        return (
          <div key={borough} className="flex flex-col gap-1">
            {boroughIndex > 0 && <Divider variant="tertiary" />}
            <TextCell className="w-full">{borough}</TextCell>

            {showAllOption && (
              <AllBoroughOption
                borough={borough}
                isSelected={allSelected}
                onToggle={() => {
                  analytics.track(
                    allSelected
                      ? 'listings_filter_borough_unselect'
                      : 'listings_filter_borough_select',
                    { boroughName: borough },
                  );
                  toggleBorough(borough);
                  onSelectResult?.();
                }}
                leadingIcon={<IconMapLocation className="w-6 h-6 icon-secondary" />}
              />
            )}

            {neighborhoods?.map(neighborhood => {
              const isSelected =
                Array.isArray(neighborhoodIds) && neighborhoodIds.includes(neighborhood.id);
              const hasChildren =
                Array.isArray(neighborhood.childrenIds) && neighborhood.childrenIds.length > 0;
              return (
                <NeighborhoodItem
                  key={neighborhood.id}
                  neighborhood={neighborhood}
                  isSelected={isSelected}
                  leadingIcon={<IconLocation className="w-6 h-6 icon-secondary" />}
                  onToggle={() => {
                    analytics.track(
                      isSelected
                        ? 'listings_filter_neighborhood_unselect'
                        : 'listings_filter_neighborhood_select',
                      { neighborhoodId: neighborhood.id, neighborhoodName: neighborhood.name },
                    );
                    if (hasChildren) toggleNeighborhoodWithChildren(neighborhood.id);
                    else toggleNeighborhood(neighborhood.id);
                    onSelectResult?.();
                  }}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}

export default NeighborhoodGroupedList;
